import { useEffect, useState } from "react";
import { Modal, InputNumber, Button, message, Tooltip, Tabs } from "antd";
import { useUser } from "@clerk/clerk-react";
import {
  StripeIcon,
  RightMarkIcon,
  DeliveryGuaranteeBgIcon,
} from "@/components/icons";
import { amountOptions, getItems } from "./config";
import normalizePrice from "@/utils/normalizePrice";
import { useModalStore } from "@/hooks/useModalStore";
import { getPaymentUrl } from "@/api/payment";
import { usePaymentStore } from "@/hooks/usePaymentStore";
import { payPlaceTypeLists } from "@/components/config";
import { useTranslation } from "react-i18next";
import { Alert, Drawer } from "aha-components";
import { useQuery } from "@/hooks/useQuery";
import BankTransfer from "./BankTransfer";
import { isMobile } from "@/biz/tool";

interface CampaignDetails {
  affiliateBudget?: number;
  budgetLimitMonthly?: number;
}

interface ChargeModalContentProps {
  user: any;
  t: any;
  balance?: number;
  amount: number | null;
  setAmount: (value: number | null) => void;
  handleConfirm: () => Promise<void>;
  loading: boolean;
  chargeModalInfo: any;
  showAlert: boolean;
}

function ChargeModalContent({
  user,
  t,
  balance,
  amount,
  setAmount,
  handleConfirm,
  loading,
  chargeModalInfo,
  showAlert,
}: ChargeModalContentProps) {
  return (
    <div className="w-full pb-[24px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-[40px] md:w-[56px] h-[40px] md:h-[56px] overflow-hidden rounded-full">
            <img src={user?.imageUrl} className="w-[40px] md:w-[56px]" alt="" />
          </div>
          <div className="justify-start text-neutral-900 text-sm md:text-base font-semibold  leading-normal flex flex-col gap-[6px]">
            <span>{user?.fullName}</span>
            <div className="justify-start text-font&icon-helper text-xs font-normal font-['DM_Sans'] leading-none">
              {t("components.chargeModal.availableBalance")}:{" "}
              {normalizePrice(balance ?? 0)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 h-6 rounded-full border border-[#A4F4CF] px-2 bg-[#ECFDF5] text-[#006045] text-[14px] whitespace-nowrap">
          <DeliveryGuaranteeBgIcon className="w-4 h-4 text-[#009966]" />
          <div className="justify-center text-Color-emerald-800 text-sm font-medium font-['DM_Sans'] leading-snug">
            {t("components.chargeModal.securePayment")}
          </div>
        </div>
      </div>
      <div className="mt-8">
        <Tabs
          defaultActiveKey="1"
          className="charge-modal-tabs"
          items={[
            {
              key: "1",
              label: t("components.chargeModal.onlinePayment"),
              children: (
                <div className="mt-8">
                  <div className="flex gap-6 w-full">
                    <div className="relative flex-1">
                      <Tooltip
                        title={
                          chargeModalInfo?.amount
                            ? t("components.chargeModal.tooltipMessage", {
                                amount: normalizePrice(chargeModalInfo?.amount),
                              })
                            : ""
                        }
                      >
                        <InputNumber
                          size="large"
                          placeholder={t("components.chargeModal.placeholder")}
                          onChange={(value) => setAmount(Number(value))}
                          precision={2}
                          min={0}
                          className="w-full text-neutral-900 text-2xl font-semibold  leading-normal"
                          style={{
                            border: "none",
                            boxShadow: "none",
                            borderBottom: "1px solid #EEE",
                            borderRadius: 0,
                          }}
                          controls={false}
                          value={amount}
                        />
                      </Tooltip>
                      <div className="justify-start text-black/30 text-base font-normal  leading-normal absolute right-0 bottom-1">
                        {t("components.chargeModal.currency")}
                      </div>
                    </div>
                    <Button
                      type="primary"
                      size="large"
                      disabled={!amount}
                      onClick={handleConfirm}
                      loading={loading}
                    >
                      {t("components.chargeModal.depositFunds")}
                    </Button>
                  </div>
                  <div className="flex flex-col md:flex-row md:gap-8 md:items-center mt-4">
                    <div className="inline-flex justify-start items-center gap-2 mt-1">
                      <div className="justify-start text-[#79716B] text-xs font-medium  leading-tight">
                        {t("components.chargeModal.poweredBy")}
                      </div>
                      <StripeIcon className="text-[#8B8D98]" />
                    </div>
                    <div className="justify-start mt-1">
                      <span className="text-font&icon-primary text-xs font-semibold font-['DM_Sans'] leading-none">
                        {t("components.chargeModal.bankTransfer")}
                      </span>
                      <span className="text-font&icon-helper text-xs font-medium font-['DM_Sans'] leading-none">
                        {" "}
                        {t("components.chargeModal.pleaseContact")}{" "}
                      </span>
                      <a
                        href="mailto:support@aha.inc"
                        className="text-[#2B7FFF] text-sm font-medium  leading-snug"
                      >
                        support@aha.inc.
                      </a>
                    </div>
                  </div>
                  {chargeModalInfo?.amount && showAlert && (
                    <Alert
                      type="warning"
                      className="mb-3"
                      description={t("components.chargeModal.alertMessage", {
                        amount: normalizePrice(chargeModalInfo?.amount as number),
                      })}
                    />
                  )}
                  <div className="flex flex-wrap gap-[15px] mt-8">
                    {amountOptions.map((item) => (
                      <div
                        key={item}
                        className={`w-[calc(100%/3-10px)] h-16 rounded-lg outline outline-1 outline-offset-[-1px] inline-flex flex-col justify-center items-center gap-1 cursor-pointer ${
                          amount === item
                            ? "outline-[#FB6011] bg-[#FFFAF5]"
                            : "outline-[#E5E5E5]"
                        }`}
                        onClick={() => setAmount(item)}
                      >
                        <div className="text-center justify-start text-text-color-primary text-base font-semibold  leading-normal">
                          {normalizePrice(item)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ),
            },
            {
              key: "2",
              label: t("components.chargeModal.bankTransfer1"),
              children: <BankTransfer />,
            },
          ]}
        />
      </div>

      <div className="flex flex-wrap mt-[40px] gap-y-4 gap-x-[44px]">
        {getItems(t).map((item) => (
          <div
            className="w-[309px] inline-flex justify-start items-center gap-2"
            key={item}
          >
            <div className="w-[18px] h-[18px] rounded-full flex items-center justify-center bg-[#009966]/20">
              <RightMarkIcon className="text-[#009966] w-[14px]" />
            </div>
            <div className="justify-start text-text-color-primary text-sm font-medium  leading-snug">
              {item}
            </div>
          </div>
        ))}
      </div>
      <div className="justify-start mt-[40px]">
        <span className="text-font&icon-primary text-sm font-normal font-['DM_Sans'] leading-snug">
          {t("components.chargeModal.byProceeding")}{" "}
        </span>
        <a
          className="text-font&icon-link text-sm font-normal leading-snug text-[#2B7FFF]"
          href="https://www.ahacreator.com/terms-of-service"
          target="_blank"
          rel="noreferrer"
        >
          {t("components.chargeModal.termsOfService")}
        </a>
        <span className="text-font&icon-primary text-sm font-normal leading-snug">
          &ensp;{t("components.chargeModal.forDepositDetails")}{" "}
        </span>
        <a
          className="text-font&icon-link text-sm font-normal leading-snug text-[#2B7FFF]"
          href="https://screeching-vulture-3d3.notion.site/Top-Up-Campaign-Budget-Guide-25c298a7f2908016874fc36104573bbd"
          target="_blank"
          rel="noreferrer"
        >
          {t("components.chargeModal.topUpCampaignBudgetGuide")}
        </a>
      </div>
    </div>
  );
}

export default function ChargeModal() {
  const { t } = useTranslation();
  const { user } = useUser();

  const { walletDetail } = usePaymentStore();
  const { balance } = walletDetail || {};

  const [amount, setAmount] = useState<number | null>(null);
  const {
    showChargeModal,
    setShowChargeModal,
    payPlace,
    campaignDetails,
    chargeModalInfo,
    setChargeModalInfo,
    setPayPlace,
  } = useModalStore();
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (payPlace === payPlaceTypeLists.ORDER_LIST && showChargeModal) {
      const { affiliateBudget = 0, budgetLimitMonthly = 0 } =
        (campaignDetails || {}) as CampaignDetails;
      if (affiliateBudget || budgetLimitMonthly) {
        setAmount(affiliateBudget + budgetLimitMonthly);
      }
    }
  }, [campaignDetails, payPlace, showChargeModal]);

  const [showAlert, setShowAlert] = useState(false);

  const campaignId = useQuery("campaignId");

  const handleConfirm = async () => {
    if (chargeModalInfo?.amount && amount && amount < chargeModalInfo?.amount) {
      setShowAlert(true);
      return;
    }
    setLoading(true);
    try {
      let href = window.location.href;
      if (!href.includes("?")) {
        href += `?amount=${amount}&payPlace=${payPlace}&campaignId=${campaignId}`;
      } else {
        href += `&amount=${amount}&payPlace=${payPlace}&campaignId=${campaignId}`;
      }
      const data = await getPaymentUrl({
        amount,
        payPlace,
        url: href,
      });
      const url = data?.url;

      if (data?.message) {
        message.error(data.message);
      } else if (url) {
        window.location.replace(url);
      }
    } catch (error) {
      message.error(t("components.chargeModal.failedToGetPaymentAddress"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (chargeModalInfo?.amount) {
      setAmount(chargeModalInfo?.amount);
    }
  }, [chargeModalInfo?.amount]);

  const mobile = isMobile();
  const handleClose = () => {
    setShowChargeModal(false);
    setAmount(null);
    setChargeModalInfo({
      amount: null,
    });
    setPayPlace("");
  };

  return (
    mobile ? (
      <Drawer
        title={t("components.chargeModal.title", { defaultValue: "Deposit funds" })}
        open={showChargeModal}
        onClose={handleClose}
        placement="bottom"
        width="100vw"
        height="100vh"
        bodyStyle={{
          padding: "16px",
        }}
      >
        <div className="pb-6">
          <ChargeModalContent
            user={user}
            t={t}
            balance={balance}
            amount={amount}
            setAmount={setAmount}
            handleConfirm={handleConfirm}
            loading={loading}
            chargeModalInfo={chargeModalInfo}
            showAlert={showAlert}
          />
        </div>
      </Drawer>
    ) : (
      <Modal
        title={t("components.chargeModal.title", { defaultValue: "Deposit funds" })}
        footer={null}
        width={864}
        open={showChargeModal}
        onCancel={handleClose}
        styles={{
          content: {
            padding: "0px",
            borderRadius: "24px",
            maxHeight: "80vh",
            overflow: "hidden",
          },
          header: {
            padding: "20px 32px 32px 32px",
            marginBottom: "0px",
          },
          body: {
            maxHeight: "calc(80vh - 56px)",
            overflowY: "auto",
            padding: "0px 32px 32px 32px",
          },
        }}
      >
        <ChargeModalContent
          user={user}
          t={t}
          balance={balance}
          amount={amount}
          setAmount={setAmount}
          handleConfirm={handleConfirm}
          loading={loading}
          chargeModalInfo={chargeModalInfo}
          showAlert={showAlert}
        />
      </Modal>
    )
  );
}
